import { useQuery, useMutation } from "@apollo/client/react"
import Button from "@mui/material/Button"
import Link from "@mui/material/Link"
import MenuItem from "@mui/material/MenuItem"
import { Formik } from "formik"
import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import * as Yup from "yup"

import { TextField } from "@/components/form"
import { GetOAuthSignupDataDocument, CompleteOauthSignupDocument, Country } from "@/graphql/graphql"
import i18n from "@/i18n"
import LanguageSelector from "@/pages/public/language_dropdown"
import { showErrorToast } from "@/shared/toast"
import { useStore } from "@/store"

import logo from "../../shared/logo.svg"
import backgroundImage from "../sign_in/background.jpg"

const STYLES = {
  container: "flex min-h-screen h-max w-screen bg-gray-200",
  sidebar: "h-screen w-1/3 bg-cover bg-center fixed top-0 left-0 flex items-start justify-center",
  logo: "mt-12",
  mainContent: "ml-[33%] w-2/3 min-h-screen flex flex-col p-8 relative",
  centerWrapper: "flex justify-center items-center flex-1",
  formCard: "flex flex-col justify-center items-center p-12 rounded-lg bg-white shadow-md max-w-2xl w-full",
  heading: "mb-8 mt-0 font-semibold text-4xl",
  form: "w-full",
  fieldsContainer: "flex flex-col gap-4",
  fieldRow: "flex gap-4",
  sectionHeading: "font-semibold mb-2 text-lg mt-2",
  errorText: "mt-4 text-sm text-red-600",
  submitButton: "mt-4",
  footer: "flex justify-center mt-auto pt-8",
  footerText: "text-gray-500 text-center max-w-sm text-xs",
  footerBold: "font-bold",
  languageSelector: "absolute bottom-8 right-8",
  readOnlyField: "bg-gray-100",
} as const

interface FormValues {
  phone: string
  businessName: string
  country: string
}

const validationSchema = Yup.object({
  phone: Yup.string().required(i18n.t("app.public.new_reservation.errors.phone_required")),
  businessName: Yup.string().required(i18n.t("app.settings.venues.add.errors.name_required")),
  country: Yup.string().required(i18n.t("app.settings.venues.add.errors.country_required")),
})

const OAuthSignupPage = () => {
  const [error, setError] = useState("")
  const navigate = useNavigate()
  const { setCurrentUser } = useStore()

  const { data, loading } = useQuery(GetOAuthSignupDataDocument)
  const [completeSignup] = useMutation(CompleteOauthSignupDocument)

  const oauthData = data?.oauthSignupData

  useEffect(() => {
    if (!loading && !oauthData) {
      navigate("/sign_in")
    }
  }, [loading, oauthData, navigate])

  if (loading) {
    return null
  }

  if (!oauthData) {
    return null
  }

  const initialValues: FormValues = {
    phone: "",
    businessName: "",
    country: "POLAND",
  }

  const displayName = [oauthData.firstName, oauthData.lastName].filter(Boolean).join(" ")

  const onSubmit = async (values: FormValues, { setSubmitting, setFieldError }) => {
    setError("")

    try {
      const { data: resultData } = await completeSignup({
        variables: {
          phone: values.phone,
          businessName: values.businessName,
          country: values.country as Country,
          timezone: "Europe/Warsaw",
        },
      })

      if (resultData?.completeOauthSignup?.success) {
        const user = resultData.completeOauthSignup.user
        setCurrentUser({ ...user, __typename: "CurrentUser" as const })
        navigate("/panel")
      } else if (resultData?.completeOauthSignup?.errors?.length > 0) {
        const errors = resultData.completeOauthSignup.errors
        let hasFieldErrors = false

        errors.forEach((err) => {
          if (err.path && err.path.length > 0) {
            const fieldName = err.path[0]
            setFieldError(fieldName, err.message)
            hasFieldErrors = true
          }
        })

        if (!hasFieldErrors) {
          const firstError = errors[0]
          setError(firstError.message || i18n.t("app.auth.oauth_signup.generic_error"))
        }
      } else {
        setError(i18n.t("app.auth.oauth_signup.generic_error"))
      }
    } catch (err) {
      showErrorToast()
      console.error(err)
      setError(i18n.t("app.auth.oauth_signup.generic_error"))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className={STYLES.container} data-testid="auth-oauth-signup-page">
      <div className={STYLES.sidebar} style={{ backgroundImage: `url(${backgroundImage})` }}>
        <img src={logo} alt="Tabb" className={STYLES.logo} />
      </div>

      <div className={STYLES.mainContent}>
        <div className={STYLES.centerWrapper}>
          <div className={STYLES.formCard}>
            <h4 className={STYLES.heading}>{i18n.t("app.auth.oauth_signup.header")}</h4>

            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={onSubmit}
              validateAfterSubmit={true}
            >
              {({ values, isSubmitting, handleSubmit }) => {
                const isFormValid = values.phone && values.businessName && values.country

                return (
                  <form onSubmit={handleSubmit} className={STYLES.form} data-testid="auth-oauth-signup-form">
                    <div className={STYLES.fieldsContainer}>
                      <div className={STYLES.fieldRow}>
                        <TextField
                          name="displayName"
                          label={i18n.t("app.auth.oauth_signup.name")}
                          value={displayName}
                          disabled
                          className={STYLES.readOnlyField}
                          data-testid="auth-oauth-signup-name-input"
                        />
                        <TextField
                          name="displayEmail"
                          label={i18n.t("app.auth.oauth_signup.email")}
                          value={oauthData.email}
                          disabled
                          className={STYLES.readOnlyField}
                          data-testid="auth-oauth-signup-email-input"
                        />
                      </div>

                      <TextField
                        name="phone"
                        label={i18n.t("app.auth.oauth_signup.phone")}
                        type="tel"
                        data-testid="auth-oauth-signup-phone-input"
                      />

                      <div className={STYLES.sectionHeading}>{i18n.t("app.auth.sign_up.business_name")}</div>

                      <TextField
                        name="businessName"
                        label={i18n.t("app.auth.oauth_signup.business_name")}
                        data-testid="auth-oauth-signup-business-name-input"
                      />

                      <TextField
                        name="country"
                        label={i18n.t("app.auth.oauth_signup.country")}
                        select
                        data-testid="auth-oauth-signup-country-select"
                      >
                        <MenuItem value="POLAND">Poland</MenuItem>
                      </TextField>
                    </div>

                    {!!error && (
                      <div className={STYLES.errorText} data-testid="auth-oauth-signup-error-message">
                        {error}
                      </div>
                    )}

                    <Button
                      variant="contained"
                      color="primary"
                      type="submit"
                      fullWidth
                      disabled={isSubmitting || !isFormValid}
                      className={STYLES.submitButton}
                      data-testid="auth-oauth-signup-submit-btn"
                    >
                      {isSubmitting ? "..." : i18n.t("app.auth.oauth_signup.submit")}
                    </Button>
                  </form>
                )
              }}
            </Formik>
          </div>
        </div>

        <div className={STYLES.footer} data-testid="auth-oauth-signup-footer">
          <div className={STYLES.footerText}>
            {i18n.t("app.auth.login.recaptcha_notice")}{" "}
            <span className={STYLES.footerBold}>{i18n.t("app.auth.login.tabb")}</span>{" "}
            <Link href="/privacy_policy" underline="hover" data-testid="auth-oauth-signup-privacy-link">
              {i18n.t("app.auth.login.privacy_policy")}
            </Link>{" "}
            {i18n.t("app.auth.login.and")}{" "}
            <Link href="/terms" underline="hover" data-testid="auth-oauth-signup-terms-link">
              {i18n.t("app.auth.login.terms_of_service")}
            </Link>
          </div>
        </div>

        <div className={STYLES.languageSelector}>
          <LanguageSelector variant="light" menuPlacement="above" />
        </div>
      </div>
    </div>
  )
}

export default OAuthSignupPage
